/*!
   * PatternFly Elements: PfeIcon 2.4.11
   * @license
   * Copyright 2020 Red Hat, Inc.
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 * 
*/

import PFElement from '../../pfelement-onhighway/dist/pfelement.umd';
import PfeIconSet from './icon-set.js';
import { addBuiltIns, resolveIconName } from './builtin-icon-sets.js';

/**
 * @typedef {Object} ThemeInfo
 * @property {string} path
 * @property {function | undefined} resolveIconName
 */

/**
 * Sets the id attribute on the <filter> element and points the CSS `filter` at that id.
 */
function _setRandomFilterId(el) {
  const randomId = 'filter-' + Math.random().toString().slice(2, 10);

  // set the CSS filter property to point at the given id
  el.shadowRoot.querySelector('svg image').style.filter = `url(#${randomId})`;

  // set the id attribute on the SVG filter element to match
  el.shadowRoot.querySelector('svg filter').setAttribute('id', randomId);
}

function _createIconSetHandler(el, setName) {
  return (ev) => {
    // if the set we're waiting for was added, run updateIcon again
    if (setName === ev.detail.set.name) {
      document.body.removeEventListener(PfeIcon.EVENTS.ADD_ICON_SET, el._handleAddIconSet);
      el.updateIcon();
    }
  };
}

function _iconLoad(el) {
  el.classList.remove('load-failed');
}

function _iconLoadError(el) {
  el.classList.add('load-failed');
  if (el.has_fallback) {
    el.classList.add('has-fallback');
  }
}

class PfeIcon extends PFElement {
    static get version() {
      return "2.4.11";
    }
  
    get html() {
      return `<style>:host([on=dark]){--pfe-broadcasted--text:var(--pfe-theme--color--text--on-dark, #fff);--pfe-broadcasted--link:var(--pfe-theme--color--link--on-dark, #fff);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover--on-dark, #fff);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus--on-dark, #fff);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited--on-dark, #fff);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}@media screen and (-ms-high-contrast:active),screen and (-ms-high-contrast:none){:host{color:#1e1e1d!important;color:var(--pfe-theme--color--text,#1e1e1d)!important}}:host([on=saturated]){--pfe-broadcasted--text:var(--pfe-theme--color--text--on-saturated, #fff);--pfe-broadcasted--link:var(--pfe-theme--color--link--on-saturated, #fff);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover--on-saturated, #fff);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus--on-saturated, #fff);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited--on-saturated, #fff);--pfe-broadcasted--link-decoration:underline;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:underline}@media screen and (-ms-high-contrast:active),screen and (-ms-high-contrast:none){:host{color:#1e1e1d!important;color:var(--pfe-theme--color--text,#1e1e1d)!important}}:host([on=light]){--pfe-broadcasted--text:var(--pfe-theme--color--text, #1e1e1d);--pfe-broadcasted--link:var(--pfe-theme--color--link, #004b93);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover, #004b93);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus, #004b93);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited, #004b93);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}@media screen and (-ms-high-contrast:active),screen and (-ms-high-contrast:none){:host{color:#1e1e1d!important;color:var(--pfe-theme--color--text,#1e1e1d)!important}}:host{--pfe-icon--Spacing:var(--pfe-theme--container-spacer, 16px);--pfe-icon--Size:var(--pfe-theme--icon-size, 1em);--pfe-icon--Padding:0;--pfe-icon--Color:var(--pfe-broadcasted--text, #1e1e1d);--pfe-icon--BackgroundColor:transparent;--pfe-icon--BorderColor:transparent;position:relative;display:inline-block;max-width:calc(var(--pfe-icon--Size) + (var(--pfe-icon--Padding) * 2));width:-webkit-fit-content!important;width:-moz-fit-content!important;width:fit-content!important;max-height:calc(var(--pfe-icon--Size) + (var(--pfe-icon--Padding) * 2));height:-webkit-fit-content!important;height:-moz-fit-content!important;height:fit-content!important;line-height:0}:host([data-block]){display:block;margin-bottom:var(--pfe-icon--Spacing);margin-top:var(--pfe-icon--Spacing)}:host([data-block]):first-child{margin-top:0}:host svg{width:var(--pfe-icon--Size);height:var(--pfe-icon--Size)}:host(:not(.load-failed)){vertical-align:middle;border-radius:50%;background-color:transparent;background-color:var(--pfe-icon--BackgroundColor,transparent);border:2px none transparent;border:var(--pfe-theme--ui--border-width,2px) none var(--pfe-icon--BorderColor,transparent);padding:0;padding:var(--pfe-icon--Padding,0)}@media screen and (-ms-high-contrast:active),screen and (-ms-high-contrast:none){:host(:not(.load-failed)) svg image{-webkit-filter:none;filter:none}}:host(:not(.load-failed)) filter feFlood{flood-color:var(--pfe-icon--Color)}:host(:not(.load-failed)) .pfe-icon--fallback{display:none}:host([size="2x"]){--pfe-icon--Size:2em}:host([size="3x"]){--pfe-icon--Size:3em}:host([size="4x"]){--pfe-icon--Size:4em}:host([size=xl]){--pfe-icon--Size:100px}:host([size=lg]){--pfe-icon--Size:64px}:host([size=md]){--pfe-icon--Size:32px}:host([size="24"]){--pfe-icon--Size:24px}:host([size="18"]){--pfe-icon--Size:18px}:host([size=sm]){--pfe-icon--Size:14px}:host([color=base]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-base, #fff)}:host([color=base-text]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-base--text, #004b93)}:host([color=complement]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-complement, #004b93)}:host([color=complement-text]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-complement--text, #fff)}:host([color=accent]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-accent, #53b7e8)}:host([color=accent-text]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-accent--text, #1e1e1d)}:host([color=disabled]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-disabled, #d0d0d0)}:host([color=disabled-text]){--pfe-broadcasted--text:var(--pfe-theme--color--ui-disabled--text, #a2a2a2)}:host([color=critical]){--pfe-broadcasted--text:var(--pfe-theme--color--feedback--critical, #c71515)}:host([color=important]){--pfe-broadcasted--text:var(--pfe-theme--color--feedback--important, #b50d0d)}:host([color=moderate]){--pfe-broadcasted--text:var(--pfe-theme--color--feedback--moderate, #de9521)}:host([color=success]){--pfe-broadcasted--text:var(--pfe-theme--color--feedback--success, #40ab00)}:host([color=info]){--pfe-broadcasted--text:var(--pfe-theme--color--feedback--info, #004b93)}:host([color=default]){--pfe-broadcasted--text:var(--pfe-theme--color--feedback--default, #a2a2a2)}:host([circled]){--pfe-icon--BackgroundColor:transparent;--pfe-icon--BorderColor:var(--pfe-theme--color--surface--border, #1e1e1d);--pfe-icon--Padding:0.05em}:host([circled=base]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--base, #a2a2a2);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text, #1e1e1d);--pfe-broadcasted--link:var(--pfe-theme--color--link, #004b93);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover, #004b93);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus, #004b93);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited, #004b93);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}:host([circled=lightest]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--lightest, #fff);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text, #1e1e1d);--pfe-broadcasted--link:var(--pfe-theme--color--link, #004b93);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover, #004b93);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus, #004b93);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited, #004b93);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}:host([circled=light]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--lighter, #d0d0d0);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text, #1e1e1d);--pfe-broadcasted--link:var(--pfe-theme--color--link, #004b93);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover, #004b93);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus, #004b93);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited, #004b93);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}:host([circled=dark]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--darker, #6f6f6f);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text--on-dark, #fff);--pfe-broadcasted--link:var(--pfe-theme--color--link--on-dark, #fff);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover--on-dark, #fff);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus--on-dark, #fff);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited--on-dark, #fff);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}:host([circled=darkest]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--darkest, #1e1e1d);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text--on-dark, #fff);--pfe-broadcasted--link:var(--pfe-theme--color--link--on-dark, #fff);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover--on-dark, #fff);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus--on-dark, #fff);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited--on-dark, #fff);--pfe-broadcasted--link-decoration:none;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:none}:host([circled=complement]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--complement, #004b93);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text--on-saturated, #fff);--pfe-broadcasted--link:var(--pfe-theme--color--link--on-saturated, #fff);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover--on-saturated, #fff);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus--on-saturated, #fff);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited--on-saturated, #fff);--pfe-broadcasted--link-decoration:underline;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:underline}:host([circled=accent]){--pfe-icon--BackgroundColor:var(--pfe-theme--color--surface--accent, #53b7e8);--pfe-icon--BorderColor:transparent;--pfe-broadcasted--text:var(--pfe-theme--color--text--on-saturated, #fff);--pfe-broadcasted--link:var(--pfe-theme--color--link--on-saturated, #fff);--pfe-broadcasted--link--hover:var(--pfe-theme--color--link--hover--on-saturated, #fff);--pfe-broadcasted--link--focus:var(--pfe-theme--color--link--focus--on-saturated, #fff);--pfe-broadcasted--link--visited:var(--pfe-theme--color--link--visited--on-saturated, #fff);--pfe-broadcasted--link-decoration:underline;--pfe-broadcasted--link-decoration--hover:underline;--pfe-broadcasted--link-decoration--focus:underline;--pfe-broadcasted--link-decoration--visited:underline}:host(.load-failed){--pfe-icon--BackgroundColor:transparent;--pfe-icon--BorderColor:transparent;--pfe-icon--Padding:0}:host(.load-failed) ::slotted(:first-child){margin-top:0}:host(.load-failed) ::slotted(:last-child){margin-bottom:0}:host(.load-failed) svg image{display:none}:host(.load-failed.has-fallback) svg,:host(.load-failed[on-fail=collapse]) svg{display:none}:host(.load-failed[on-fail=collapse]){--pfe-icon--Size:0}:host([in-cta]){--pfe-icon--Color:var(--pfe-cta--Color, var(--pfe-icon--Color--in-cta));--pfe-icon--Color--in-cta:var(--pfe-broadcasted--link, #004b93);--pfe-icon--Size:16px;margin:0;margin:var(--pfe-icon--Margin,0)}:host([in-cta][cta-priority]){--pfe-icon--Size:20px}:host([in-cta][cta-priority=primary]){--pfe-icon--Color--in-cta:#1e1e1d}:host([in-cta][cta-priority=secondary]){--pfe-icon--Color--in-cta:var(--pfe-theme--color--text, #1e1e1d)}:host([in-cta][cta-priority=tertiary]){--pfe-icon--Color--in-cta:#fff}:host([in-cta][cta-priority=floating]){--pfe-icon--Color--in-cta:#004b93}:host([in-cta=left]){--pfe-icon--Margin:0 7px 0 0}:host([in-cta=left][cta-priority]){--pfe-icon--Margin:0 13px 0 0}:host([in-cta=right][cta-priority]){--pfe-icon--Margin:0 0 0 13px}
/*# sourceMappingURL=pfe-icon.min.css.map */
</style><div class="pfe-icon--fallback">
  <slot></slot>
</div>
<svg xmlns="http://www.w3.org/2000/svg">
  <filter color-interpolation-filters="sRGB" x="0" y="0" height="100%" width="100%">
    <feFlood result="COLOR" />
    <feComposite operator="in" in="COLOR" in2="SourceAlpha" />
  </filter>
  <image xlink:href="" width="100%" height="100%"></image>
</svg>`;
    }
  
    static get properties() {
      return {"icon":{"title":"Icon","type":"string","prefixed":false},"size":{"title":"Size","type":"string","enum":["default","xl","lg","md","sm","2x","3x","4x","24"],"default":"default","prefixed":false},"color":{"title":"Color","type":"string","enum":["default","base","complement","accent","critical","important","moderate","success","info"],"prefixed":false},"circled":{"title":"Circled","type":"boolean","prefixed":false}};
    }
  
    static get slots() {
      return {};
    }
  static get tag() {
    return 'pfe-icon';
  }

  get templateUrl() {
    return 'pfe-icon.html';
  }

  get styleUrl() {
    return 'pfe-icon.scss';
  }

  get schemaUrl() {
    return 'pfe-icon.json';
  }

  get has_fallback() {
    return this.children.length > 0 || this.innerText.length > 0;
  }

  static get observedAttributes() {
    return ['icon', 'on-fail', 'color', 'cta-priority', 'in-cta'];
  }

  constructor() {
    super(PfeIcon);

    this.image = this.shadowRoot.querySelector('svg image');
    this.image.addEventListener('load', () => _iconLoad(this));
    this.image.addEventListener('error', () => _iconLoadError(this));
    this._handleAddIconSet = this._handleAddIconSet.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.body.addEventListener(PfeIcon.EVENTS.ADD_ICON_SET, this._handleAddIconSet);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.removeEventListener(PfeIcon.EVENTS.ADD_ICON_SET, this._handleAddIconSet);
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(...arguments);
    if (attr == 'icon' || attr == 'on-fail') {
      this.updateIcon(newValue);
    } else {
      if (oldValue != newValue) {
        switch (attr) {
          case 'color':
          case 'cta-priority':
          case 'in-cta':
            this.updatesStylesForIE();
            break;
          default:
            break;
        }
      }
    }
  }

  _handleAddIconSet(event) {
    /**
     * @type {PfeIconSet}
     */
    const addedSet = event.detail.set;
    const iconName = this.getAttribute('icon');
    if (addedSet.name == PfeIcon.getIconSet(iconName).setName) {
      this.updateIcon(iconName);
    }
  }

  updateIcon(iconName = this.getAttribute('icon')) {
    const { setName, set } = PfeIcon.getIconSet(iconName);

    if (set) {
      const iconPath = set.resolveIconName(iconName);
      this.image.setAttribute('xlink:href', iconPath);
      _setRandomFilterId(this);
    } else {
      this.log(`"No icon set with name ${setName} available yet"`);
    }
  }

  /**
   * Get an icon set by providing the set's name, _or_ the name of an icon from that set.
   *
   * @param {String} iconName the name of the set, or the name of an icon from that set.
   * @return {PfeIconSet} the icon set
   */
  static getIconSet(iconName) {
    const [setName] = iconName.split('-');
    const set = this._iconSets[setName];
    return { setName, set };
  }

  static addIconSet(name, path, resolveIconName) {
    if (this._iconSets[name]) {
      this.log(`a set with name ${name} already exists.`);
    }

    this._iconSets[name] = new PfeIconSet(name, path, resolveIconName);

    document.body.dispatchEvent(
      new CustomEvent(this.EVENTS.ADD_ICON_SET, {
        bubbles: false,
        detail: {
          set: this._iconSets[name],
        },
      })
    );
  }

  /**
   *
   * @param {*} name
   * @param {*} newPath
   * @param {*} resolveIconName
   */
  static updateSetPath(name, newPath, resolveIconName = undefined) {
    if (name === this.themeSetName) {
      this.setThemeSetInfo(this.pageTheme, newPath, resolveIconName);
    } else {
      this._updateSetPath(name, newPath, resolveIconName);
    }
  }

  /**
   *
   * @param {*} name
   * @param {*} newPath
   * @param {*} resolveIconName
   */
  static _updateSetPath(name, newPath, resolveIconName = undefined) {
    if (this._iconSets[name]) {
      this._iconSets[name] = new PfeIconSet(name, newPath, resolveIconName || this._iconSets[name]._resolveIconName);
      document.body.dispatchEvent(
        new CustomEvent(this.EVENTS.ADD_ICON_SET, {
          bubbles: false,
          detail: {
            set: this._iconSets[name],
          },
        })
      );
    } else {
      throw new Error(`pfe-icon: the icon set with name ${name} does not exist`);
    }
  }

  static get EVENTS() {
    return {
      ADD_ICON_SET: `${this.tag}:add-icon-set`,
    };
  }

  // ==========================================================
  // Theme set section (theme config in builin-icon-sets.js)
  // ==========================================================

  static get themeSetName() {
    return this._themeSetName;
  }

  static _updateThemeSet() {
    const currentThemeSet = this.getIconSet(this.themeSetName);
    const defaultInfo = this.defaultThemeSetInfo;
    const allThemeSetInfo = this.allThemeSetInfo;
    let themeInfo = defaultInfo;
    if (this.pageTheme && allThemeSetInfo.hasOwnProperty(this.pageTheme)) {
      themeInfo = allThemeSetInfo[this.pageTheme];
    }
    if (themeInfo.path !== currentThemeSet.path || themeInfo.resolveIconName !== currentThemeSet._resolveIconName) {
      this._updateSetPath(this.themeSetName, themeInfo.path || defaultInfo.path, themeInfo.resolveIconName || defaultInfo.resolveIconName);
    }
  }

  static _addThemeSet() {
    const defaultInfo = this.defaultThemeSetInfo;
    const allThemeSetInfo = this.allThemeSetInfo;
    let themeInfo = defaultInfo;
    if (this.pageTheme && allThemeSetInfo.hasOwnProperty(this.pageTheme)) {
      themeInfo = allThemeSetInfo[this.pageTheme];
    }
    this.addIconSet(this.themeSetName, themeInfo.path || defaultInfo.path, themeInfo.resolveIconName || defaultInfo.resolveIconName);
  }

  /**
   * @returns {ThemeInfo}
   */
  static get defaultThemeSetInfo() {
    return this._defaultThemeSetInfo;
  }

  /**
   *
   * @param {ThemeInfo} value
   */
  static set defaultThemeSetInfo(value) {
    this._defaultThemeSetInfo = value;
    this._updateThemeSet();
  }

  /**
   * @returns {ThemeInfo[]}
   */
  static get allThemeSetInfo() {
    return this._themeSetInfo;
  }

  /**
   * @param {ThemeInfo[]} value
   */
  static set allThemeSetInfo(value) {
    this._themeSetInfo = value;
    this._updateThemeSet();
  }

  static setThemeSetInfo(theme, path, resolveIconName = undefined) {
    const allThemeSetInfo = this.allThemeSetInfo;
    allThemeSetInfo[theme] = { path, resolveIconName };
    this.allThemeSetInfo = allThemeSetInfo;
  }

  static _themeSetManager() {
    // prevent multiple initializations
    if (!this._themeChangeObserver) {
      this._themeChangeObserver = new MutationObserver(() => {
        this._updateThemeSet();
      });
      this._addThemeSet();
      this._themeChangeObserver.observe(document.body, { attributes: true, attributeFilter: [this.themeAttrName] });
    }
  }

  // ==========================================================
  // Section to be moved to the pfelement @todo
  // ==========================================================
  static get themeAttrName() {
    return 'glob-theme';
  }
  static get pageTheme() {
    return document.body.getAttribute(this.themeAttrName);
  }

  updatesStylesForIE() {
    if (window.ShadyCSS && window.ShadyCSS.styleSubtree) {
      window.ShadyCSS.styleSubtree(this);
    }
  }
  // ==========================================================
}

PfeIcon._iconSets = {};

addBuiltIns(PfeIcon);

PfeIcon._themeSetManager();

PFElement.create(PfeIcon);

export default PfeIcon;
